import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material";
import React, { FC, useState } from "react";

import { ariaLabel } from "src/constants";
import { isActivationKey } from "src/utils/activationKey";

import { ListColumnsConfig } from "../index";
import { ListSelectionCheckbox } from "./SelectionCheckbox";

export type ListRowProps<T = any, U = any> = {
  data: T;
  dataType?: string;
  columnsConfig: ListColumnsConfig<T>;
  isSelected: boolean;
  isMaxSelectedReached: boolean;
  onToggle: (data: T, checked: boolean) => void;
  onRowClick?: (data: T, extraProps: U) => void;
  expandComponent?: FC<{ data: T; extraProps: U }>;
  extraProps?: U;
  rowIndex: number;
};

export const StyledExpandWrapper = styled("td")`
  background-color: ${(props) => props.theme.palette.table.expendedRow};
  padding: ${(props) => props.theme.spacing(2)};
`;

type StyledRowProps = {
  isSelected: boolean;
  isExpanded: boolean;
  onClick?: () => void;
} & Pick<ListRowProps, "columnsConfig">;

const StyledListRow = styled("tr")<StyledRowProps>`
  background-color: ${(props) => props.theme.palette.common.white};
  border-bottom: 1px solid ${(props) => props.theme.palette.table.expendedRowListBorder};
  text-align: left;
  color: ${(props) => props.theme.palette.text.primary};
  cursor: ${(props) => (props.onClick ? "pointer" : "auto")};

  ${(props) => {
    if (props.isSelected || props.isExpanded) {
      return `
        & {
          background-color : ${props.theme.palette.primary.light};
          
           &:focus {
                outline: 2px solid ${props.theme.palette.blue.focus};
            }
        }
      `;
    }

    if (props.onClick) {
      return `
            &:hover {
                background-color : ${props.theme.palette.primary.light};
            }
            
            &:focus {
                outline: 2px solid ${props.theme.palette.blue.focus};
            }
        `;
    }

    return "";
  }}

  ${(props) =>
    props.columnsConfig.map((c, index) => {
      // Column Width
      let widthValue = "auto";
      if (c.type === "selection" || c.type === "expand") {
        widthValue = "4%";
      } else if (c.width) {
        widthValue = `${c.width}%`;
      }

      // TextAlign
      let textAlign = "left";
      if (c.rowAlign) {
        textAlign = c.rowAlign;
      }

      return `& > td:nth-of-type(${index + 1}) {
        max-width: ${widthValue};
        text-align: ${textAlign};
      }`;
    })}

  > td {
    padding: ${(props) =>
      props.theme.spacing(props.columnsConfig.find((c) => c.type === "selection") ? 1 : 2)};

    word-break: break-word;
    font-weight: 500;
  }
`;

export const ListDefaultRow = React.memo(
  ({
    data,
    dataType,
    columnsConfig,
    isSelected,
    isMaxSelectedReached,
    onToggle,
    onRowClick,
    expandComponent,
    extraProps,
    rowIndex,
  }: ListRowProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const ExpandComponent = expandComponent;

    let onClick: (() => void) | undefined;
    let handleKeyDown: ((event: React.KeyboardEvent<HTMLTableRowElement>) => void) | undefined;
    if (ExpandComponent) {
      onClick = () => setIsExpanded(!isExpanded);
      handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (isActivationKey(event)) {
          event.preventDefault();
          setIsExpanded(!isExpanded);
        }
      };
    } else if (onRowClick) {
      onClick = () => onRowClick(data, extraProps);
      handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (isActivationKey(event)) {
          event.preventDefault();
          onRowClick(data, extraProps);
        }
      };
    }

    return (
      <>
        <StyledListRow
          columnsConfig={columnsConfig}
          onClick={onClick}
          isSelected={isSelected}
          isExpanded={isExpanded}
          aria-expanded={ExpandComponent ? isExpanded : undefined}
          data-cy-rowtype={dataType}
          id={`list-row-${rowIndex}`}
          onKeyDown={handleKeyDown}
          tabIndex={onClick ? 0 : undefined}
        >
          {columnsConfig.map((c, index) => {
            let content = null;
            if (c.type === "selection") {
              const isDocumentData = dataType?.includes("document");
              const checkBoxAriaLabel = isDocumentData
                ? `${ariaLabel.documentListSelectCheckbox} - ${data.originalFilename}`
                : `${ariaLabel.listSelectCheckbox} ${rowIndex}`;
              content = (
                <ListSelectionCheckbox
                  id={`checkbox-item-${rowIndex}`}
                  ariaLabel={checkBoxAriaLabel}
                  isMaxSelectedReached={isMaxSelectedReached}
                  checked={isSelected}
                  onChange={(checked) => {
                    onToggle(data, checked);
                  }}
                />
              );
            } else if (c.type === "expand") {
              content = isExpanded ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />;
            } else {
              content = c.render(data, extraProps);
            }

            return <td key={index}>{content}</td>;
          })}
        </StyledListRow>
        {isExpanded && ExpandComponent && (
          <tr id={`list-row-expand-${rowIndex}`} data-cy-rowtype={dataType}>
            <StyledExpandWrapper colSpan={columnsConfig.length}>
              <ExpandComponent data={data} extraProps={extraProps} />
            </StyledExpandWrapper>
          </tr>
        )}
      </>
    );
  }
);
